/* eslint-disable jsx-a11y/media-has-caption */
import { Checkbox, DatePicker, Select, Tag } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";

import { ReactComponent as ICON_ARROW } from "@assets/svgs/ICON_ARROW.svg";
import { ReactComponent as ICON_TABLE_DELETE } from "@assets/svgs/ICON_TABLE_DELETE.svg";
import ErrorValidate from "@components/ErrorValidate";
import { Icon } from "@components/Icon";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import {
  COLOR_DARK,
  COLOR_GREY,
  COLOR_GREY_1,
  COLOR_RED,
} from "@static/COLORS";
import { DEFAULT_DURATION } from "@static/DEFAULT_DURATION";
import { onHandleDeleteBlockMedia } from "@utils/mediaBlockEditor/onHandleDeleteBlockMedia";
import {
  onHandleDurationBlur,
  onHandleDurationChange,
} from "@utils/mediaBlockEditor/onhandleInputDuration";
import {
  onHandleRepeatBlur,
  onHandleRepeatChange,
} from "@utils/mediaBlockEditor/onhandleInputRepeat";
import { onHandleSwapBlockMediaIndex } from "@utils/mediaBlockEditor/onHandleSwapBlockMediaIndex";
import { VALIDATE_RULES } from "@validate/index";
const { RangePicker } = DatePicker;

const { REACT_APP_API_ENDPOINT } = process.env;

const Container = styled.div`
  width: 100%;
  flex-shrink: 0;
  border: 1px solid ${({ borderColor = COLOR_GREY_1 }) => borderColor};
  border-radius: 8px;
  display: flex;
  padding: 16px;
  gap: 16px;
  justify-content: center;
  box-sizing: border-box;
`;

const SectionContents = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const AudioContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  aspect-ratio: 16/9;
`;

const SectionDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 8px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background: #e2e2e2;
`;

const ContainerName = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const SectionInput = styled.div`
  /* display: flex;
  flex-direction: row; */
  display: grid;
  grid-template-columns: 15% 15% auto 10%;
  gap: 8px;
`;

const SectionAction = styled.div`
  display: flex;
  gap: 8px;
  height: 100%;
  justify-content: space-between;
  flex-direction: column;
`;

const ContainerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const ContainerControlAction = styled.div`
  width: 100%;
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
`;

const ShowExpireContainer = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
`;

const GroupInput = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const EditorAudio = ({
  item,
  index,
  blockMediaList = [],
  mediaListAudioKey = {},
  setBlockMediaList = () => undefined,
}) => {
  const initialIsNoEnd = dayjs(_.get(item, ["end_date"])).isSame(
    "3000-01-01",
    "day"
  );
  const [isNoEnd, setIsNoEnd] = useState(initialIsNoEnd);
  const [repeat, setRepeat] = useState(_.get(item, ["repeat"], 1));

  const { watch, control, handleSubmit } = useForm({
    defaultValues: {
      dateRange: [
        dayjs(_.get(item, ["start_date"])),
        dayjs(_.get(item, ["end_date"])),
      ],
    },
  });

  const path = `${REACT_APP_API_ENDPOINT}/${_.get(item, [
    "attribute",
    "path",
  ])}`;

  const [duration, setDuration] = useState(
    _.get(item, ["duration"], DEFAULT_DURATION)
  );

  const onHandleChangeValue = ({ key, value }) => {
    const updatedItem = _.set({ ...item }, key, value);
    setBlockMediaList((prevList) => {
      const newList = [...prevList];
      newList[index] = updatedItem;
      return newList;
    });
  };

  const onHandleChangeDateRange = (value) => {
    const date_range_start_raw = _.get(value, [0]);
    const date_range_end_raw = _.get(value, [1]);

    const start_date = dayjs(date_range_start_raw).format("YYYY-MM-DD");
    const end_date = dayjs(date_range_end_raw).format("YYYY-MM-DD");
    const updatedItem = {
      ...item,
      start_date,
      end_date,
    };
    setBlockMediaList((prevList) => {
      const newList = [...prevList];
      newList[index] = updatedItem;
      return newList;
    });
  };

  const dateRange = watch("dateRange");
  const currentDate = dayjs();
  const endDate = _.get(dateRange, [1]);
  const isExpired = currentDate.isAfter(endDate);
  const expiredDays = isExpired ? currentDate.diff(endDate, "day") : 0;

  const onHandleNoEndChange = (checked) => {
    setIsNoEnd(checked);
    const start_date = checked
      ? dayjs("1990-01-01").format("YYYY-MM-DD")
      : dayjs().subtract(1, "day").format("YYYY-MM-DD");
    const end_date = checked
      ? dayjs("3000-01-01").format("YYYY-MM-DD")
      : dayjs().add(1, "month").format("YYYY-MM-DD");
    const updatedItem = {
      ...item,
      start_date,
      end_date,
    };
    setBlockMediaList((prevList) => {
      const newList = [...prevList];
      newList[index] = updatedItem;
      return newList;
    });
  };

  const mediaId = _.get(item, ["media_id"]);

  return (
    <Container borderColor={isExpired ? COLOR_RED : COLOR_GREY}>
      <SectionContents>
        <AudioContainer>
          <audio
            controls
            type={_.get(item, ["type"])}
            src={path}
            style={{ width: "100%" }}
          />
        </AudioContainer>
        <SectionDetails>
          <ContainerName>
            <Text
              style={{ width: "calc(100% - 140px)" }}
              isEllipsis
              lines={1}
              align="start"
              color="#2B2F32"
              fontSize={"14px"}
              fontWeight="600"
            >
              {_.get(mediaListAudioKey, [mediaId, "display_name"])}
            </Text>
            {isExpired && (
              <Text
                style={{ width: "auto" }}
                align="center"
                color="#F41C0C"
                fontSize={"14px"}
                fontWeight="600"
              >
                {`Expired ( ${expiredDays} days )`}
              </Text>
            )}
          </ContainerName>
          <Line />
          <SectionInput>
            <ContainerField>
              <Text color={COLOR_DARK} fontWeight="400" fontSize="14px">
                {_.startCase("duration")}
              </Text>
              <StyledInput
                value={duration}
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(e) =>
                  onHandleDurationChange({ event: e, setDuration })
                }
                onBlur={(e) =>
                  onHandleDurationBlur({
                    event: e,
                    setDuration,
                    onHandleChangeValue,
                  })
                }
              />
            </ContainerField>
            <ContainerField>
              <Text color={COLOR_DARK} fontWeight="400" fontSize="14px">
                {_.startCase("Repeat")}
              </Text>
              <StyledInput
                value={repeat}
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(e) =>
                  // onHandleDurationChange({ event: e, setRepeat })
                  onHandleRepeatChange({ event: e, setRepeat })
                }
                onBlur={(e) =>
                  onHandleRepeatBlur({
                    event: e,
                    setRepeat,
                    onHandleChangeValue,
                  })
                }
              />
            </ContainerField>

            <ContainerField>
              <Controller
                name="dateRange"
                control={control}
                rules={_.get(VALIDATE_RULES, ["required"])}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Text
                      color={COLOR_DARK}
                      fontWeight="400"
                      fontSize="14px"
                      style={{ marginBottom: 4 }}
                    >
                      Date Range
                    </Text>
                    <RangePicker
                      {...field}
                      disabled={isNoEnd}
                      className={isNoEnd && "picker-no-limit"}
                      style={{ width: "100%" }}
                      allowClear={false}
                      onChange={(value) => {
                        field.onChange(value);
                        onHandleChangeDateRange(value);
                      }}
                    />
                  </div>
                )}
              />
            </ContainerField>
            <CheckboxContainer>
              <Text color={COLOR_DARK} fontSize="14px">
                Forever
              </Text>
              <Checkbox
                style={{ marginTop: 10 }}
                checked={isNoEnd}
                onChange={(e) => onHandleNoEndChange(e.target.checked)}
              />
            </CheckboxContainer>
          </SectionInput>
        </SectionDetails>
        <SectionAction>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Icon
              disabled={index === 0}
              isCursor
              width={24}
              height={24}
              isRotated
              onClick={() =>
                onHandleSwapBlockMediaIndex({
                  type: "up",
                  index,
                  setBlockMediaList,
                })
              }
            >
              <ICON_ARROW />
            </Icon>
            <Icon
              disabled={index === _.size(blockMediaList) - 1}
              isCursor
              width={24}
              height={24}
              onClick={() =>
                onHandleSwapBlockMediaIndex({
                  type: "down",
                  index,
                  setBlockMediaList,
                })
              }
            >
              <ICON_ARROW />
            </Icon>
          </div>

          <Icon
            isCursor
            width={24}
            height={24}
            onClick={() =>
              onHandleDeleteBlockMedia({
                index,
                blockMediaList,
                setBlockMediaList,
              })
            }
          >
            <ICON_TABLE_DELETE />
          </Icon>
        </SectionAction>
      </SectionContents>
    </Container>
  );
};
